// One-line "12.4 km · 340 m up · 2h 0m" summary for a recording row.
//
// Extracted rather than left inline in the component so the formatting boundaries are
// testable: which parts appear, what rounds to what, and how a duration under an hour
// reads. Same reasoning as the sibling deletedRecordingRetention helper.

/** Shape is loose on purpose — GraphQL hands these back nullable. */
export interface RecordingStatsSource {
  distanceMeters?: number | null
  elevationGainMeters?: number | null
  elapsedSeconds?: number | null
}

const SEPARATOR = ' · '

function isPositive (value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

/** Metres to a one-decimal kilometre string. */
export function formatDistance (distanceMeters: number): string {
  return `${(distanceMeters / 1000).toFixed(1)} km`
}

/** Whole metres climbed. "up" rather than a unit alone, so it can't read as distance. */
export function formatElevationGain (elevationGainMeters: number): string {
  return `${Math.round(elevationGainMeters)} m up`
}

/**
 * Hours and minutes, dropping the hours entirely below one hour so a short walk reads
 * "42m" rather than "0h 42m". Seconds are never shown — this is a summary line.
 */
export function formatDuration (elapsedSeconds: number): string {
  const hours = Math.floor(elapsedSeconds / 3600)
  const minutes = Math.floor((elapsedSeconds % 3600) / 60)

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

/**
 * Joins whichever parts the recording actually has. A zero, a null or a missing value is
 * omitted rather than rendered as "0.0 km" — a recording with no track should show
 * nothing, not a row of zeroes. Returns '' when there is nothing to say.
 */
export function recordingStatsSummary (recording: RecordingStatsSource | null | undefined): string {
  if (!recording) return ''

  const parts: string[] = []

  if (isPositive(recording.distanceMeters)) parts.push(formatDistance(recording.distanceMeters))
  if (isPositive(recording.elevationGainMeters)) parts.push(formatElevationGain(recording.elevationGainMeters))
  if (isPositive(recording.elapsedSeconds)) parts.push(formatDuration(recording.elapsedSeconds))

  return parts.join(SEPARATOR)
}
